import { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import {
  marketingService,
  AnalyticsFilters,
  AnalyticsOverview,
  CampaignAnalyticsRow,
  AnalyticsTimeseries,
  Campaign,
  CampaignDevice,
} from '../../api/services/marketingService';

export const useMarketingAnalytics = () => {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [rows, setRows] = useState<CampaignAnalyticsRow[]>([]);
  const [timeseries, setTimeseries] = useState<AnalyticsTimeseries | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [dateRange, setDateRange] = useState<[string, string] | null>([
    dayjs().subtract(30, 'day').startOf('day').toISOString(),
    dayjs().endOf('day').toISOString(),
  ]);
  const [campaignId, setCampaignId] = useState<string | undefined>();
  const [device, setDevice] = useState<CampaignDevice | undefined>();
  const [country, setCountry] = useState<string | undefined>();

  const buildFilters = useCallback((): AnalyticsFilters => {
    const filters: AnalyticsFilters = {};
    if (dateRange) {
      filters.dateFrom = dateRange[0];
      filters.dateTo = dateRange[1];
    }
    if (campaignId) filters.campaignId = campaignId;
    if (device) filters.device = device;
    if (country) filters.country = country;
    return filters;
  }, [dateRange, campaignId, device, country]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const filters = buildFilters();
    try {
      const [ov, perCampaign, ts] = await Promise.all([
        marketingService.getOverview(filters),
        marketingService.getPerCampaign(filters),
        marketingService.getTimeseries(filters),
      ]);
      setOverview(ov);
      setRows(perCampaign);
      setTimeseries(ts);
    } catch {
      message.error('Failed to load marketing analytics');
    } finally {
      setLoading(false);
    }
  }, [buildFilters]);

  const fetchCampaigns = useCallback(async () => {
    try {
      setCampaigns(await marketingService.getCampaigns());
    } catch {
      /* non-fatal — filter select just stays empty */
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return {
    overview,
    rows,
    timeseries,
    campaigns,
    loading,
    dateRange,
    campaignId,
    device,
    country,
    setDateRange,
    setCampaignId,
    setDevice,
    setCountry,
    refetch: fetchAll,
  };
};
